import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Box,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  LocalShipping as TruckIcon,
  Person as PersonIcon,
  Speed as SpeedIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Work as WorkIcon,
  LocalGasStation as FuelIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import MapboxMiniMap from './MapboxMiniMap';
import TruckJobSummary, { truckJobHeadingKey } from './TruckJobSummary';
import { reverseGeocodeCached } from '../../utils/geocoding';

const TruckDetailsModal = ({
  open,
  onClose,
  truck,
  allTrucks = []
}) => {
  const { t } = useTranslation();
  const [address, setAddress] = useState('');
  const [loadingAddress, setLoadingAddress] = useState(false);

  useEffect(() => {
    if (truck && truck.latitude && truck.longitude) {
      fetchAddress(truck.latitude, truck.longitude);
    }
  }, [truck]);

  const fetchAddress = async (lat, lng) => {
    if (!lat || !lng) return;
    
    setLoadingAddress(true);
    try {
      const geocodedAddress = await reverseGeocodeCached(lat, lng);
      setAddress(geocodedAddress);
    } catch (error) {
      console.error('Error fetching address:', error);
      setAddress(`${lat.toFixed(4)}, ${lng.toFixed(4)}`);
    } finally {
      setLoadingAddress(false);
    }
  };

  const getTruckStatus = (truck) => {
    if (!truck.ignition_status || truck.ignition_status === 'Off') return { status: 'offline', color: 'default', text: 'Offline' };
    
    if (truck.ignition_status === 'On') {
      if (truck.speed_kph > 5) {
        return { status: 'driving', color: 'info', text: 'Driving' };
      } else if (truck.job_in_progress) {
        return { status: 'working', color: 'warning', text: 'On Site' };
      } else {
        return { status: 'idle', color: 'default', text: 'Idle' };
      }
    }
    
    return { status: 'parked', color: 'success', text: 'Parked' };
  };

  const formatLastUpdate = (lastUpdate) => {
    if (!lastUpdate) return 'No data';
    const diff = new Date() - new Date(lastUpdate);
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ago`;
  };

  if (!truck) return null;

  const status = getTruckStatus(truck);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box display="flex" alignItems="center">
          <TruckIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            {truck.truck_name} - Live Tracking
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, height: '100%' }}>
        <Grid container sx={{ height: '100%' }}>
          {/* Map Section */}
          <Grid size={{ xs: 12, md: 8 }} sx={{ height: '100%' }}>
            <MapboxMiniMap
              latitude={truck.latitude}
              longitude={truck.longitude}
              status={status.status}
              speed={truck.speed_kph}
              height="100%"
              truckName={truck.truck_name}
            />
          </Grid>
          
          {/* Details Panel */}
          <Grid size={{ xs: 12, md: 4 }} sx={{ height: '100%', overflow: 'auto', p: 2 }}>
            {/* Truck Status Card */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    {truck.truck_name}
                  </Typography>
                  <Chip
                    label={status.text}
                    color={status.color}
                    size="small"
                  />
                </Box>
                
                {truck.fence_vehicle_name && (
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    GPS Device: {truck.fence_vehicle_name}
                  </Typography>
                )}

                <Box display="flex" alignItems="center" mb={1}>
                  <SpeedIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    Speed: {Math.round(truck.speed_kph || 0)} km/h
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" mb={1}>
                  <TimeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    Last Update: {formatLastUpdate(truck.last_update)}
                  </Typography>
                </Box>

                {truck.fuel_level_percentage !== null && (
                  <Box mt={2}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <FuelIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        Fuel: {Math.round(truck.fuel_level_percentage)}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={truck.fuel_level_percentage} 
                      sx={{ height: 6, borderRadius: 3 }}
                      color={truck.fuel_level_percentage > 25 ? 'primary' : 'error'}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Driver Info Card */}
            {truck.driver_first_name && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Driver Information
                  </Typography>
                  <Box display="flex" alignItems="center" mb={1}>
                    <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {truck.driver_first_name} {truck.driver_last_name}
                    </Typography>
                  </Box>
                  {truck.driver_username && (
                    <Typography variant="body2" color="textSecondary">
                      Username: {truck.driver_username}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Current / Next Job Card */}
            {(truck.current_job_id || truck.last_job_number) && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <WorkIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="h6">
                      {t(truckJobHeadingKey(truck))}
                    </Typography>
                  </Box>
                  <TruckJobSummary truck={truck} />
                </CardContent>
              </Card>
            )}

            {/* Location Card */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Location
                </Typography>
                <Box display="flex" alignItems="flex-start" mb={1}>
                  <LocationIcon sx={{ mr: 1, color: 'text.secondary', mt: 0.2 }} />
                  <Box>
                    {loadingAddress ? (
                      <Typography variant="body2" color="textSecondary">
                        Loading address...
                      </Typography>
                    ) : (
                      <Typography variant="body2">
                        {address}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Typography variant="caption" color="textSecondary">
                  Coordinates: {truck.latitude?.toFixed(4)}, {truck.longitude?.toFixed(4)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default TruckDetailsModal;
